import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Loader, Toggle } from "rsuite";

import { useAuth } from "context/auth-context";
import { useConfig } from "../context/config-context";
import { useModal } from "../context/modal-context";
import { useAlert } from "../context/alert-context";

import Gabarit from "../components/Gabarit";
import { InputConfirmPassword, InputPassword } from "../components/Inputs";
import UpdateMobilePhoneModal from "../components/Modal/UpdateMobilePhoneModal";
import UpdateEmailModal from "../components/Modal/UpdateEmailModal";
import UpdateOtpChannelModal from "../components/Modal/UpdateOtpChannelModal";

import API from "../utils/api";

// Modales d'édition du compte : sans barre d'en-tête, fermeture au clic sur le fond
const ACCOUNT_MODAL_OPTIONS = { hideHeader: true, closeOnOutsideClick: true };

const OTP_CHANNEL_LABELS = {
  sms: "Envoi du code par SMS",
  email: "Envoi du code par mail",
};

/**
 * Champ en lecture seule de la carte "Informations du compte",
 * avec un bouton d'édition optionnel à droite de la valeur
 */
function AccountField({ label, value, onEdit, editTitle }) {
  const siteConfig = useConfig();

  return (
    <div>
      <label className="text-base font-semibold text-neutral-dark block mb-1">
        {label}
      </label>
      <div className="flex items-center w-fit gap-2">
        <p className="text-base text-neutral-dark break-all">{value || "- -"}</p>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
            title={editTitle}
          >
            <img
              src={siteConfig.medias.edit_svg.path}
              alt={siteConfig.medias.edit_svg.alt}
              width={18}
              height={18}
            />
          </button>
        )}
      </div>
    </div>
  );
}

export default function AccountPage() {
  const [loading, setLoading] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [initialNotificationsEnabled, setInitialNotificationsEnabled] = useState(true);
  const [otpChannel, setOtpChannel] = useState(null);
  const { showAlert } = useAlert();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const siteConfig = useConfig();
  const { user, refreshUser } = useAuth();
  const { showModal } = useModal();

  const password = watch("password");

  // Charger les préférences de notifications au montage
  useEffect(() => {
    if (user?.role === "prescriber") {
      fetchNotificationPreferences();
    }
  }, [user]);

  /**
   * Récupère les préférences de notifications du prescripteur
   */
  const fetchNotificationPreferences = async () => {
    try {
      const response = await API.get("/prescriber/preferences", { silent: true });
      if (response.data.success) {
        const enabled = response.data.data.notificationsEnabled ?? true;
        setNotificationsEnabled(enabled);
        setInitialNotificationsEnabled(enabled);
        setOtpChannel(response.data.data.otpChannel);
      }
    } catch (error) {
      console.error("Erreur récupération préférences:", error);
    }
  };

  /**
   * Met à jour l'état local du toggle (sans sauvegarder)
   */
  const handleToggleChange = (checked) => {
    setNotificationsEnabled(checked);
  };

  /**
   * Enregistre les préférences de notifications
   */
  const handleSaveNotificationPreferences = async () => {
    try {
      setLoadingPreferences(true);

      const response = await API.put("/prescriber/preferences", {
        notificationsEnabled,
      }, { silent: true });

      if (response.data.success) {
        setInitialNotificationsEnabled(notificationsEnabled);
        showAlert("success", "Vos préférences de notification ont bien été enregistrées.");
      }
    } catch (error) {
      console.error("Erreur mise à jour préférences:", error);
      showAlert("error", error.response?.data?.error?.message || "Erreur lors de l'enregistrement des préférences.");
    } finally {
      setLoadingPreferences(false);
    }
  };

  /**
   * Soumet le formulaire de changement de mot de passe
   */
  const onSubmitPassword = async (data) => {
    try {
      setLoading(true);
      await API.put("/users/reset-password", data);
      showAlert("success", "Votre mot de passe a bien été renouvelé.");
    } catch (error) {
      // Les erreurs sont gérées par l'interceptor axios (toast)
    } finally {
      setLoading(false);
    }
  };

  /**
   * Met à jour les données du compte après une modification réussie depuis une modale
   */
  const handleProfileUpdated = (message) => (profile) => {
    refreshUser();
    setOtpChannel(profile.otpChannel);
    showAlert("success", message);
  };

  /**
   * Ouvre la modale de modification de l'email
   */
  const handleEditEmail = () => {
    showModal(
      <UpdateEmailModal
        currentEmail={user.email}
        onSuccess={handleProfileUpdated("L’email a bien été mis à jour.")}
      />,
      ACCOUNT_MODAL_OPTIONS
    );
  };

  /**
   * Ouvre la modale de modification du téléphone mobile
   */
  const handleEditMobilePhone = () => {
    showModal(
      <UpdateMobilePhoneModal
        currentPhone={user.mobilePhone}
        onSuccess={handleProfileUpdated(
          "Le numéro de téléphone mobile a bien été mis à jour."
        )}
      />,
      ACCOUNT_MODAL_OPTIONS
    );
  };

  /**
   * Ouvre la modale de choix de la méthode de confirmation d'identité
   */
  const handleEditOtpChannel = () => {
    showModal(
      <UpdateOtpChannelModal
        currentChannel={otpChannel}
        hasMobilePhone={!!user.mobilePhone}
        onSuccess={handleProfileUpdated(
          "La méthode de confirmation d’identité a bien été mise à jour."
        )}
      />,
      ACCOUNT_MODAL_OPTIONS
    );
  };

  return (
    <Gabarit
      label={siteConfig.pages.account.title}
      className="flex flex-col space-y-2"
    >
      {/* Layout en 3 colonnes (1/3 + 2/3) */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Colonne gauche : Informations du compte (1/3) */}
        <div className="flex flex-col p-5 h-fit space-y-4 bg-white shadow-lg rounded-xl">
          <h2 className="text-lg font-semibold text-primary border-b pb-2">
            Informations du compte
          </h2>

          <div className="space-y-3">
            {/* Nom et Prénom */}
            <div className="grid grid-cols-2 gap-4">
              <AccountField label="Nom" value={user.lastName} />
              <AccountField label="Prénom" value={user.firstName} />
            </div>

            {user?.role === "prescriber" ? (
              <>
                <AccountField
                  label="Email"
                  value={user.email}
                  onEdit={handleEditEmail}
                  editTitle="Modifier l'email"
                />
                <AccountField
                  label="Téléphone mobile"
                  value={user.mobilePhone}
                  onEdit={handleEditMobilePhone}
                  editTitle="Modifier le numéro de téléphone mobile"
                />
                <AccountField
                  label="Méthode de confirmation d’identité"
                  value={OTP_CHANNEL_LABELS[otpChannel]}
                  onEdit={otpChannel ? handleEditOtpChannel : undefined}
                  editTitle="Modifier la méthode de confirmation d'identité"
                />

                <p className="text-sm italic text-neutral-dark pt-2">
                  Le numéro de téléphone renseigné est utilisé exclusivement
                  dans le cadre du portail prescripteur et ne fait l’objet
                  d’aucune autre utilisation.
                </p>
              </>
            ) : (
              <>
                <AccountField label="Email" value={user.email} />
                <AccountField
                  label="Adresse"
                  value={
                    user.location?.street &&
                    user.location?.zipCode &&
                    user.location?.city &&
                    `${user.location.street}, ${user.location.zipCode} ${user.location.city}`
                  }
                />
                <div className="grid grid-cols-2 gap-4">
                  <AccountField label="Téléphone fixe" value={user.linePhone} />
                  <AccountField
                    label="Téléphone mobile"
                    value={user.mobilePhone}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Colonne droite : Préférences + Mot de passe (2/3) */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Préférences de notifications (prescripteurs uniquement) */}
          {user?.role === "prescriber" && (
            <div className="flex flex-col p-5 space-y-4 h-fit bg-white shadow-lg rounded-xl">
              <h2 className="text-lg font-semibold text-primary border-b pb-2">
                Préférences de notifications
              </h2>

              <div className="space-y-3">
                <p className="text-sm text-neutral-dark">
                  Vous avez la possibilité d'activer ou de désactiver la
                  notification par mail pour les mails suivants :
                </p>

                {/* Section Nouveaux documents */}
                <div className="pt-1">
                  <h3 className="text-base font-semibold text-neutral-dark mb-2.5">
                    Nouveaux documents
                  </h3>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-neutral-dark">
                      Recevoir un mail pour la mise à disposition de nouveaux
                      documents de votre file active :{" "}
                    </span>
                    <span className="font-semibold text-sm text-neutral-dark mr-2">
                      {notificationsEnabled ? "Activé" : "Désactivé"}
                    </span>
                    <Toggle
                      checked={notificationsEnabled}
                      onChange={handleToggleChange}
                      disabled={loadingPreferences}
                    />
                  </div>
                </div>

                {/* Bouton d'enregistrement */}
                <div className="flex justify-end pt-4 border-t border-t-gray-200">
                  <button
                    onClick={handleSaveNotificationPreferences}
                    disabled={
                      loadingPreferences ||
                      notificationsEnabled === initialNotificationsEnabled
                    }
                    className="px-[12.5px] py-[6px] bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    {loadingPreferences ? (
                      <Loader />
                    ) : (
                      "Enregistrer les préférences"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Renouvellement du mot de passe */}
          <div className="flex flex-col p-5 space-y-4 h-fit bg-white shadow-lg rounded-xl">
            <h2 className="text-lg font-semibold text-primary border-b pb-2">
              Renouvellement du mot de passe
            </h2>

            <form
              onSubmit={handleSubmit(onSubmitPassword)}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <InputPassword
                  id="password"
                  label="Nouveau mot de passe"
                  placeholder="Nouveau mot de passe"
                  register={register}
                  error={errors.password}
                />
                <InputConfirmPassword
                  id="confirmPassword"
                  label="Confirmer le mot de passe"
                  placeholder="Confirmer le mot de passe"
                  password={password}
                  register={register}
                  error={errors.confirmPassword}
                />
              </div>

              <div className="flex justify-end pt-4 border-t border-t-gray-200">
                <button
                  type="submit"
                  className="px-[12.5px] py-[6px] bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors font-semibold"
                  disabled={
                    loading || !watch("password") || !watch("confirmPassword")
                  }
                >
                  {loading ? <Loader /> : "Valider"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Gabarit>
  );
}
